import { Constructor } from '@angular/cdk/table';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { BehaviorSubject, EMPTY, Subject, forkJoin, of } from 'rxjs';
import { catchError, map, takeUntil } from 'rxjs/operators';
import { IContentList } from 'src/app/core/models/i-content-list';
import { compareArrays } from '../utils/array-utils';
import { IContentListDto } from 'src/app/core/dtos/interfaces/i-content-list-dto';
import { ContentListService } from 'src/app/content-list-management/shared/services/cmsapi/content-list.service';
import { HttpErrorResponse } from '@angular/common/http';

type WithContentListFormSectionType = {
  _initContentList(contentList: IContentListDto[]): void;
  _initContentListValueChanges(
    getPreviousSelectedValue: () => IContentListDto[] | null
  ): void;
  _buildContentList(): IContentListDto[];
};

type ContentListFormSectionRequerimentsBase = {
  form: FormGroup;
  formReady: BehaviorSubject<boolean>;
  FORM_PATHS: { CONTENT_LISTS: () => string };
  destroy$: Subject<boolean>;
  contentListService: ContentListService;
};

export function WithContentListFormSection<
  TBase extends Constructor<ContentListFormSectionRequerimentsBase>
>(
  baseClass: TBase
): {
  new (...args: any[]): WithContentListFormSectionType;
  prototype: WithContentListFormSectionType;
} & TBase {
  return class WithContentListFormSectionable extends baseClass {
    constructor(...args: any[]) {
      super(...args);
    }

    _initContentList(contentList: IContentListDto[]): void {
      const contentListControl = this.form.get(this.FORM_PATHS.CONTENT_LISTS());
      const requests$ = contentList.map((i) =>
        this.contentListService.getById$(i.id).pipe(
          catchError((error: HttpErrorResponse) => {
            if (error.status === 404) {
              return of(null);
            }
            throw error;
          })
        )
      );

      forkJoin(requests$)
        .pipe(
          map((responses) => {
            responses = responses.filter((response) => response !== null);
            return [...responses].sort((a, b) => a.name.localeCompare(b.name));
          })
        )
        .subscribe({
          next: (sortedResponse) => {
            sortedResponse.forEach((contentList) => {
              (contentListControl as FormArray<FormControl<IContentList>>).push(
                new FormControl(contentList)
              );
            });
            contentListControl.markAsPristine();
            this.formReady.next(true);
          },
        });
    }

    _initContentListValueChanges(
      getPreviousSelectedValue: () => IContentListDto[] | null
    ): void {
      this.form
        .get(this.FORM_PATHS.CONTENT_LISTS())
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (newValue) => {
            if (compareArrays(newValue, getPreviousSelectedValue())) {
              this.form.get(this.FORM_PATHS.CONTENT_LISTS()).markAsPristine();
            } else {
              this.form.get(this.FORM_PATHS.CONTENT_LISTS()).markAsDirty();
            }
          },
        });
    }

    _buildContentList(): IContentListDto[] {
      return this.form.get(this.FORM_PATHS.CONTENT_LISTS()).value;
    }
  };
}
